import React from "react";
import { useTranslation } from "react-i18next";
import { Globe } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { SUPPORTED_LANGUAGES, type SupportedLanguage } from "@/i18n";

const LABELS: Record<SupportedLanguage, string> = {
  en: "English",
  ja: "日本語",
  de: "Deutsch",
};

const STORAGE_KEY = "ii_lang";

const persistLanguage = (lng: SupportedLanguage) => {
  try {
    window.localStorage.setItem(STORAGE_KEY, lng);
  } catch {
    /* storage may be unavailable (private mode) — ignore */
  }
};

const LanguageSwitcher: React.FC = () => {
  const { i18n, t } = useTranslation("common");
  const current = (i18n.language?.startsWith("ja")
    ? "ja"
    : i18n.language?.startsWith("de")
      ? "de"
      : "en") as SupportedLanguage;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        className="inline-flex items-center gap-2 rounded-md border border-border bg-card px-3 py-2 text-sm text-foreground/80 hover:text-white hover:bg-secondary transition-colors"
        aria-label={t("language")}
      >
        <Globe className="h-4 w-4" />
        <span className="font-sans">{LABELS[current]}</span>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="bg-card border-border">
        {SUPPORTED_LANGUAGES.map((lng) => (
          <DropdownMenuItem
            key={lng}
            onClick={() => {
              persistLanguage(lng);
              void i18n.changeLanguage(lng);
            }}
            className={`cursor-pointer ${current === lng ? "text-brand-green" : "text-foreground/90"}`}
          >
            {LABELS[lng]}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default LanguageSwitcher;